import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

const SLIDE_VISIBLITY_THRESHOLD = 0.7;

export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /* -------------------------
    REQUIRED REFS
  ------------------------- */
  requiredRefs = ['scroller', 'slideshowContainer'];

  /* -------------------------
    LIFECYCLE
  ------------------------- */
  async connectedCallback() {
    super.connectedCallback();

    if (viewTransition.current) {
      await viewTransition.current;
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1
      ? this.#setupSlideshowWithoutControls()
      : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#scroll) {
      this.refs.scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
  }

  /* -------------------------
    GETTERS
  ------------------------- */
  get slides() {
    return this.refs.slides?.filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );
  }

  get initialSlideIndex() {
    const value = parseInt(this.getAttribute('initial-slide') || '0', 10);
    return isNaN(value) ? 0 : value;
  }

  get visibleSlides() {
    return this.#visibleSlides;
  }

  /* -------------------------
    CURRENT SLIDE (FIXED)
  ------------------------- */
  #current = 0;

  get current() {
    return this.#current;
  }

  set current(value) {
    this.#current = value;

    const { thumbnails, dots, previous, next, slides } = this.refs;

    for (const controls of [thumbnails, dots]) {
      if (!controls) continue;

      const items = Array.isArray(controls) ? controls : [controls];
      items.forEach((el, i) =>
        el.setAttribute('aria-selected', String(i === value))
      );
    }

    if (previous) previous.disabled = !this.infinite && value === 0;
    if (next) next.disabled = !this.infinite && slides && value >= slides.length - 1;
  }

  get infinite() {
    return this.hasAttribute('infinite');
  }

  /* -------------------------
    SETUP
  ------------------------- */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    this.slides?.[0]?.setAttribute('aria-hidden', 'false');
  }

  #setupSlideshow() {
    this.#setupIntersectionObserver();

    const { scroller } = this.refs;

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: () => this.setAttribute('transitioning', ''),
      onScrollEnd: () => this.removeAttribute('transitioning'),
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.current = this.initialSlideIndex;

    scheduler.schedule(() => {
      requestAnimationFrame(() => {
        this.#scroll.to(this.slides[this.current], { instant: true });
      });

      this.#resizeObserver = new ResizeObserver(() => {
        this.#updateVisibleSlides();
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /* -------------------------
    SCROLL HANDLERS
  ------------------------- */
  #handleScroll = () => {
    const index = this.#sync();
    if (index !== this.current) {
      this.current = index;
    }
  };

  #sync() {
    const { slides } = this;
    if (!slides?.length) return 0;

    const visible = this.visibleSlides;
    if (!visible.length) return this.current;

    const centers = visible.map((s) => center(s, this.#scroll.axis));
    const ref = center(this.refs.scroller, this.#scroll.axis);
    const closestCenter = closest(centers, ref);

    return slides.indexOf(visible[centers.indexOf(closestCenter)]);
  }

  /* -------------------------
    DRAG SUPPORT
  ------------------------- */
  #handleMouseDown = (event) => {
    if (this.disabled) return;
    event.preventDefault();
  };

  /* -------------------------
    INTERSECTION OBSERVER
  ------------------------- */
  #intersectionObserver;
  #visibleSlides = [];

  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const slide = entry.target;
          const isVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (isVisible && !this.#visibleSlides.includes(slide)) {
            this.#visibleSlides.push(slide);
          } else if (!isVisible) {
            this.#visibleSlides = this.#visibleSlides.filter((s) => s !== slide);
          }
        }

        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
      }
    );

    slides.forEach((slide) => this.#intersectionObserver.observe(slide));
  }

  #updateVisibleSlides() {
    this.slides?.forEach((slide) => {
      slide.setAttribute(
        'aria-hidden',
        String(!this.#visibleSlides.includes(slide))
      );
    });

    return this.#visibleSlides.length;
  }

  /* -------------------------
    INTERNALS
  ------------------------- */
  #scroll;
  #resizeObserver;
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
